import { useState, useEffect, useCallback, useRef } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { format, isPast, parseISO } from "date-fns"
import { formatDate } from "@/utils/formatDate"
import { isInscricaoEncerrada } from "@/utils/date"
import { ptBR } from "date-fns/locale"
import { Building2, MapPin, User, ArrowLeft } from "lucide-react"

import {
  getCalendarioAction,
  subscribeEvent,
  updateEventZoomLink,
} from "@/modules/Events/service/EventsService"
import { fetchMembrosAction } from "@/modules/Users/service/UsersService"
import { EventoDataType } from "@/types/events"
import { NetworkMembroItem, NetworkMeta } from "@/types/membros"

import { useAuth } from "@/modules/Auth/context/AuthContext"
import { useLoading } from "@/hooks/useLoading"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { FichaTecnicaPanel } from "@/modules/Events/components/FichaTecnicaPanel"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { MemberModal } from "@/modules/Users/components/MemberModal"
import { BatchSubscribeModal } from "@/modules/Events/components/BatchSubscribeModal"
import { CalendarSyncModal } from "@/modules/Calendar/components/CalendarSyncModal"
import { useCalendarIntegration } from "@/modules/Calendar/hooks/useCalendarIntegration"
import { useIsMobile } from "@/hooks/use-mobile"
import { useSubscribeConfirm } from "@/modules/Events/context/SubscribeConfirmContext"
import { imagebossCover, imagebossOriginal } from "@/utils/imageboss"
import { toast } from "sonner"

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { state: locationState } = useLocation()
  const readOnly = (locationState as any)?.readOnly ?? false
  const eventFromState = (locationState as any)?.evento as EventoDataType | null
  const { user } = useAuth()
  const { showLoading, hideLoading } = useLoading()

  const [evento, setEvento] = useState<EventoDataType | null>(eventFromState ?? null)
  const [zoomLink, setZoomLink] = useState("")
  const [zoomSaving, setZoomSaving] = useState(false)
  const [selectedMemberId, setSelectedMemberId] = useState<string | undefined>()
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false)
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false)
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false)
  const { googleConnected, microsoftConnected } = useCalendarIntegration()
  const isMobile = useIsMobile()

  const { requestSubscribe } = useSubscribeConfirm()

  // Participants
  const [participants, setParticipants] = useState<NetworkMembroItem[]>([])
  const [participantsMeta, setParticipantsMeta] = useState<NetworkMeta | null>(null)
  const [participantsPage, setParticipantsPage] = useState(1)
  const [participantsLoading, setParticipantsLoading] = useState(true)
  const [participantsLoadingMore, setParticipantsLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const fetchEvento = useCallback(() => {
    if (!user) return
    showLoading()
    getCalendarioAction({ emp_id: user.empId, pes_id: user.pesId, limit: 100 })
      .then(({ data }) => {
        const found = data.data.find((ev: EventoDataType) => String(ev.id) === String(id))
        if (found) {
          setEvento(found)
          setZoomLink(found.even_link_zoom ?? "")
        } else if (!eventFromState) {
          navigate("/events")
        }
      })
      .catch(() => navigate("/events"))
      .finally(() => hideLoading())
  }, [user, id, eventFromState])

  useEffect(() => {
    fetchEvento()
  }, [fetchEvento])

  const fetchParticipants = useCallback(
    async (pageNum: number) => {
      if (!evento) return
      if (pageNum === 1) setParticipantsLoading(true)
      else setParticipantsLoadingMore(true)
      try {
        const { data } = await fetchMembrosAction({
          id_origem: evento.id,
          origem: evento.origem.toLowerCase(),
          page: pageNum,
          limit: 10,
        })
        setParticipants((prev) => (pageNum === 1 ? data.data : [...prev, ...data.data]))
        setParticipantsMeta(data.meta)
      } catch (err) {
        console.error(err)
      } finally {
        if (pageNum === 1) setParticipantsLoading(false)
        else setParticipantsLoadingMore(false)
      }
    },
    [evento],
  )

  useEffect(() => {
    if (evento) fetchParticipants(1)
  }, [evento])

  useEffect(() => {
    if (participantsPage === 1) return
    fetchParticipants(participantsPage)
  }, [participantsPage, fetchParticipants])

  useEffect(() => {
    if (!sentinelRef.current || !participantsMeta?.hasMore || participantsLoadingMore) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setParticipantsPage((p) => p + 1)
      },
      { threshold: 0.1 },
    )
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [participantsMeta?.hasMore, participantsLoadingMore])

  const handleSubscribe = () => {
    if (!evento) return
    requestSubscribe(evento, fetchEvento)
  }

  if (!evento) return null

  const isEventOpen = !isInscricaoEncerrada(evento.data_limite) && !isPast(new Date(evento.data_inicio))
  const isSubscribable = !evento.inscrito && isEventOpen && !readOnly
  const canBatchSubscribe = user.pesAdmin && isEventOpen && !readOnly
  const canManageFile = user.gestaoApp && !readOnly

  const totalParticipants = participantsMeta?.total ?? participants.length

  return (
    <div className="w-full overflow-x-hidden pb-8">
      {/* Header */}
      <div className="flex items-center gap-2 p-4 pb-2 sm:gap-3 sm:p-6 sm:pb-2">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-surface-elevated hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </button>
        <div className="flex min-w-0 items-center gap-2 overflow-hidden text-sm text-muted-foreground">
          <span
            className="shrink-0 cursor-pointer hover:text-foreground"
            onClick={() => navigate(-1)}
          >
            Eventos
          </span>
          <span className="shrink-0">/</span>
          <span className="truncate text-foreground">{evento.descricao}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 px-4 pt-4 sm:px-6 xl:grid-cols-[3fr_2fr] xl:grid-rows-[auto_1fr]">
          {/* Card principal */}
          <div className="overflow-hidden rounded-xl border border-border bg-surface xl:col-start-1 xl:row-start-1">

            {/* Hero — imagem com título sobreposto */}
            <div className="relative aspect-[2/1] w-full overflow-hidden">
              {evento.eve_foto_url || evento.ime_foto_url ? (
                <img
                  src={imagebossOriginal(evento.eve_foto_url || evento.ime_foto_url) ?? (evento.eve_foto_url || evento.ime_foto_url)!}
                  alt={evento.descricao}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-surface-elevated">
                  <i className="fi fi-ts-calendar-image text-5xl text-muted" />
                </div>
              )}
              {/* Gradient overlay + título */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 flex flex-col items-start justify-between gap-3 p-4 sm:p-6">
                {evento.inscrito && (
                  <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500/20 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-emerald-400 ring-1 ring-emerald-500/30 backdrop-blur-sm">
                    <i className="fi fi-ts-check-circle flex items-center" />
                    Inscrito
                  </span>
                )}
                <h1 className="min-w-0 text-xl font-bold leading-tight text-white drop-shadow sm:text-2xl">
                  {evento.descricao}
                </h1>
              </div>
            </div>

            {/* Corpo — data âncora + detalhes */}
            <div className="flex gap-0 divide-x divide-border">

              {/* Coluna de data */}
              <div className="flex w-28 shrink-0 flex-col items-center justify-center gap-0.5 px-4 py-5 sm:w-32">
                <span className="text-4xl font-black leading-none text-primary sm:text-5xl">
                  {format(parseISO(evento.data_inicio), "dd")}
                </span>
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {format(parseISO(evento.data_inicio), "MMM", { locale: ptBR })}
                </span>
                <span className="mt-1 text-xs text-muted-foreground">
                  {format(parseISO(evento.data_inicio), "yyyy")}
                </span>
                <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                  <i className="fi fi-ts-clock flex items-center text-[11px]" />
                  <span>{format(parseISO(evento.data_inicio), "HH:mm")}</span>
                </div>
                {evento.data_inicio.slice(0, 10) !== evento.data_fim.slice(0, 10) && (
                  <div className="mt-1 text-center text-[10px] leading-tight text-muted">
                    até {format(parseISO(evento.data_fim), "dd MMM", { locale: ptBR })}
                  </div>
                )}
              </div>

              {/* Coluna de detalhes */}
              <div className="flex min-w-0 flex-1 flex-col justify-between gap-4 p-4 sm:p-5">
                <div className="flex flex-col gap-2.5 text-sm">
                  {(evento.local || evento.endereco) && (
                    <div className="flex items-start gap-2">
                      <i className="fi fi-ts-marker mt-0.5 flex shrink-0 items-center text-sm text-primary" />
                      <div className="min-w-0">
                        <p className="truncate text-foreground">{evento.local}</p>
                        {evento.endereco && (
                          <p className="truncate text-xs text-muted-foreground">{evento.endereco}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {evento.data_limite && (
                    <div className="flex items-center gap-2">
                      <i className="fi fi-ts-hourglass-end flex shrink-0 items-center text-sm text-primary" />
                      <span className="text-xs text-muted-foreground">
                        {isInscricaoEncerrada(evento.data_limite)
                          ? "Inscrições encerradas"
                          : `Inscrições até ${format(new Date(evento.data_limite), "dd/MM/yyyy")}`}
                      </span>
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {!isMobile && evento.inscrito && (googleConnected || microsoftConnected) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => setIsCalendarModalOpen(true)}
                    >
                      <i className="fi fi-ts-calendar-lines flex" />
                      Adicionar à agenda
                    </Button>
                  )}
                  {canBatchSubscribe && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-primary hover:bg-primary/10 hover:text-primary"
                      onClick={() => setIsBatchModalOpen(true)}
                    >
                      <i className="fi fi-ts-login mr-1.5 flex items-center" />
                      Inscrever participantes
                    </Button>
                  )}
                  {evento.inscrito && evento.even_link_zoom ? (
                    <Button
                      className="bg-gradient-to-tr flex-1 from-blue-900 to-blue-500 font-bold text-blue-100 shadow-lg transition-all hover:scale-[1.02] hover:shadow-blue-600/40"
                      onClick={(e) => {
                        e.stopPropagation()
                        window.open(evento.even_link_zoom!, "_blank", "noopener,noreferrer")
                      }}
                    >
                      <i className="fi fi-ts-video-camera mr-1.5 flex items-center" />
                      Entrar
                    </Button>
                  ) : (
                    <Button
                      variant={isSubscribable ? "default" : "secondary"}
                      disabled={!isSubscribable}
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSubscribe()
                      }}
                    >
                      {evento.inscrito ? "Inscrito" : isEventOpen ? "Inscrever-me" : "Inscrições encerradas"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

        {/* Membros inscritos — DOM order 2nd so on mobile it appears between card and accordions */}
        <div className="flex max-h-[480px] flex-col overflow-hidden rounded-xl border border-border bg-surface xl:col-start-2 xl:row-span-2 xl:row-start-1 xl:sticky xl:top-4 xl:max-h-[calc(100vh-120px)]">
          <div className="border-b border-border p-4 sm:p-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <i className="fi fi-ts-users flex items-center text-base text-primary" />
              Membros
            </h2>
            <p className="text-sm text-muted-foreground">
              {totalParticipants} {totalParticipants === 1 ? "inscrito" : "inscritos"}
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {participantsLoading ? (
              <p className="py-10 text-center text-muted-foreground">Carregando inscritos...</p>
            ) : participants.length > 0 ? (
              <div className="flex flex-col gap-3">
                {participants.map((participant) => (
                  <div
                    key={participant.pesUid}
                    onClick={() => {
                      setSelectedMemberId(participant.pesUid)
                      setIsMemberModalOpen(true)
                    }}
                    className="cursor-pointer rounded-lg border bg-card p-4 text-card-foreground shadow-sm transition-colors hover:border-primary/50"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary">
                        {participant.pesFotoUrl ? (
                          <img
                            src={imagebossCover(participant.pesFotoUrl, 40, 40, "face") ?? participant.pesFotoUrl}
                            alt={participant.pesNome}
                            className="h-full w-full rounded-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none"
                              e.currentTarget.nextElementSibling?.removeAttribute("style")
                            }}
                          />
                        ) : null}
                        <User
                          className="h-5 w-5 text-muted-foreground"
                          style={participant.pesFotoUrl ? { display: "none" } : undefined}
                        />
                      </div>
                      <div className="space-y-1 overflow-hidden">
                        <p className="font-medium leading-none">{participant.pesNome}</p>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Building2 className="h-3 w-3 shrink-0" />
                          <p className="truncate">{participant.empFantasia}</p>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <p className="truncate">{participant.empCidade}, {participant.empEstado}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={sentinelRef} className="py-2 text-center text-sm text-muted-foreground">
                  {participantsLoadingMore && "Carregando mais..."}
                </div>
              </div>
            ) : (
              <p className="py-10 text-center text-muted-foreground">Nenhum inscrito encontrado.</p>
            )}
          </div>
        </div>

          {/* Accordions */}
          <Accordion type="multiple" defaultValue={["ficha", "zoom"]} className="flex flex-col gap-4 xl:col-start-1 xl:row-start-2">
            <div className="overflow-hidden rounded-xl border border-purple-500/30 bg-purple-500/5">
              <AccordionItem value="ficha" className="border-0 px-4">
                <AccordionTrigger className="text-base font-semibold text-purple-500 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <i className="fi fi-ts-document flex items-center text-base text-purple-500" />
                    Ficha Técnica
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-4 pt-2">
                    <FichaTecnicaPanel
                      evento={evento}
                      canManage={canManageFile}
                      onUpdate={(url) => setEvento((prev) => prev ? { ...prev, url_ficha_tecnica: url } : prev)}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </div>

            {user.gestaoApp && !readOnly && (
              <div className="overflow-hidden rounded-xl border border-blue-500/30 bg-blue-500/5">
                <AccordionItem value="zoom" className="border-0 px-4">
                  <AccordionTrigger className="text-base font-semibold text-blue-500 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <i className="fi fi-ts-video-camera flex items-center text-base text-blue-500" />
                      Link do Zoom
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col gap-4 pb-2 pt-2">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Link do Zoom</label>
                        <div className="flex gap-2">
                          <Input
                            className="flex-1"
                            placeholder="https://zoom.us/j/..."
                            value={zoomLink}
                            onChange={(e) => setZoomLink(e.target.value)}
                          />
                          <Button
                            disabled={zoomSaving}
                            onClick={async () => {
                              setZoomSaving(true)
                              try {
                                await updateEventZoomLink(evento.id, zoomLink)
                                toast.success("Link do Zoom atualizado!")
                                fetchEvento()
                              } catch {
                                toast.error("Erro ao salvar link do Zoom.")
                              } finally {
                                setZoomSaving(false)
                              }
                            }}
                          >
                            {zoomSaving ? "Salvando..." : "Salvar"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </div>
            )}
          </Accordion>
      </div>


      <MemberModal
        isOpen={isMemberModalOpen}
        onClose={() => setIsMemberModalOpen(false)}
        memberId={selectedMemberId}
      />

      <BatchSubscribeModal
        isOpen={isBatchModalOpen}
        event={evento}
        onBatchSubscribe={() => {
          fetchEvento()
        }}
        onClose={() => setIsBatchModalOpen(false)}
      />
      
      <CalendarSyncModal
        isOpen={!isMobile && isCalendarModalOpen}
        evento={evento}
        onClose={() => setIsCalendarModalOpen(false)}
      />
    </div>
  )
}
